/**
 * The main user survey controller. This reacts to the user survey status as to what is shown on the DOM, it also is
 * the router for userSurveyService interaction
 *
 *
 * If / when the Welcome screen comes back into use...
 *
 * ```
 {[UNSEEN, SEEN].includes(status) && (
   <Welcome
     id={idNamespace}
     userSurvey={userSurvey}
     onMount={() => this.updateUserSurvey(this.state.userSurvey, {status: SEEN})}
     onStart={() => this.updateUserSurvey(this.state.userSurvey, {status: STARTED}, true)} />
 )}
 * ```
 *
 *
 *
 * @author Matthew Rawson @ 09/02/2018 15:59
 * @description
 * @notes
 */
import React, {Component} from "react"
import PropTypes from "prop-types"
import {get} from "lodash"
import styled from "styled-components"

import withUnauthed from "@irati/auth/helpers/auth/withUnauthed"
import id from "@irati/utils/id"
import connect from "@irati/store/connect"
import Loading from "@irati/ui/src/Loading"
import {STATUS_OPEN, STATUS_OPENING, STATUS_CLOSED} from "@irati/insights/constants/survey"
import {Http404} from "@irati/ui/src/Errors"
import userSurveyService from "@irati/insights/services/userSurvey"
import {FINISHED, SEEN, STARTED, UNSEEN} from "@irati/insights/constants/userSurveyStatus"
import {getUserSurvey} from "@irati-web/insights/src/graph/userSurveys"
import commentservice from "@irati/insights/services/comment"
import {userSurveyTracking} from "@irati-tracking/insights"
import intercom from "@irati/utils/intercom"

import {Unknown} from "../../components/Errors"
import {Expired, Finish} from "../../components/Finish"
import {QuestionContainer} from "../../components/Question"
import {updateDriverList} from "../../store/actions/userSurvey"

const OPEN_STATUSES = [STATUS_OPEN, STATUS_OPENING]
const SURVEY_CLOSED_ERROR = "PBSUR_USSVN0000"

const Wrapper = styled.div`
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`

class UserSurvey extends Component {
  static propTypes = {
    match: PropTypes.object,
    auth: PropTypes.object,
    survey: PropTypes.object,
    dispatchDriverList: PropTypes.func.isRequired
  }
  static defaultProps = {
    match: {},
    auth: {}
  }

  constructor(props) {
    super(props)

    intercom.hide()

    this.state = {
      initiallyFinished: false,
      load: new Promise(() => undefined),
      tenant: {
        public_name: ""
      }
    }
  }

  componentDidMount() {
    /** First run fetch the user survey allow a retry initially */
    this.init(true)
  }

  resetLoad = () => {
    setTimeout(() => {
      this.setState({load: new Promise(() => undefined)})
    })
  }

  retryInit(doRetry) {
    if (doRetry) {
      this.resetLoad()
      setTimeout(() => this.init(), 2000)
    }
  }

  isSurveyOpen = ({surveyInfo = {}, test = false}) => {
    return OPEN_STATUSES.includes(surveyInfo.status) || test
  }

  isSurveyClosed = ({surveyInfo = {}, test = false}) => {
    return surveyInfo.status === STATUS_CLOSED && !test
  }

  isUserSurveyYetToBeOpened = ({status}) => {
    return [UNSEEN, SEEN].includes(status)
  }

  init(doRetry = false) {
    const {
      auth: {
        unauthedBusiness: {uuid}
      }
    } = this.props
    const fetchUserSurvey = getUserSurvey(this.getSurveyId(), uuid, {withQuestions: true})
      .then((userSurvey) => {
        const {surveyInfo} = userSurvey

        this.props.dispatchDriverList(userSurvey.drivers.results)

        /** Small flag to determine if this is a user returning after previously completing the survey or not */
        if (userSurvey.status === FINISHED) this.setState({initiallyFinished: true})

        /** The survey will always return if the id is valid, however we need to check the tenants match */
        if (surveyInfo.tenant !== uuid) {
          throw new Error("Tenant mismatch. Survey not found.")
        }

        /** stop further processing if the survey isn't open */
        if (!this.isSurveyOpen(userSurvey)) {
          return this.setState({userSurvey})
        }

        /** Without a Welcome page the user survey needs to be automatically started */
        if (this.isUserSurveyYetToBeOpened(userSurvey)) {
          return this.updateUserSurvey(userSurvey, {status: STARTED}, true)
            .then(() => userSurveyTracking.started({userSurvey}))
            .catch(() => this.retryInit(doRetry))
        }

        this.setState({userSurvey})
      })
      .catch((e) => {
        if (e.errorCode === SURVEY_CLOSED_ERROR) {
          this.setState({userSurvey: {surveyInfo: {status: STATUS_CLOSED}}})
        } else {
          throw new Error(e)
        }
      })

    this.setState({
      load: fetchUserSurvey
    })
  }

  /**
   * Return the survey id from the url
   *
   * @returns {string}
   */
  getSurveyId() {
    return get(this.props.match, "params.surveyId")
  }

  /**
   * Generic patch and update user survey object
   *
   * @param status
   * @returns {Promise<any>}
   */
  updateUserSurvey(userSurvey, data, syncLoadToState = false) {
    const {
      auth: {
        unauthedBusiness: {uuid}
      }
    } = this.props
    const patch = userSurveyService.patchAndFetchUserSurvey(uuid, userSurvey, data)
    patch.then((newUserSurvey) => {
      this.setState({userSurvey: newUserSurvey})
    })

    if (syncLoadToState) {
      this.setState({
        load: patch
      })
    }

    return patch
  }

  /**
   * Save the user comment to the database (if one is stored in redux)
   *
   * @param syncLoadToState
   * @returns {Promise<any>}
   */
  createUserSurveyComment = (syncLoadToState = false) => {
    const {
      survey,
      auth: {
        unauthedBusiness: {uuid}
      }
    } = this.props
    const {userSurvey} = this.state
    const {comment} = survey

    const create = commentservice.postUserSurveyComment(uuid, userSurvey, comment).then(() => {
      userSurveyTracking.comment({userSurvey, comment})
    })

    if (syncLoadToState) {
      this.setState({
        load: create
      })
    }

    return create
  }

  /**
   {![STARTED, FINISHED].includes(status) && <Unknown id={idNamespace} />} */

  render() {
    const idNamespace = id("user-survey", this.props, {force: true})
    const {userSurvey = {}} = this.state
    const {status} = userSurvey
    const {
      auth: {
        unauthedBusiness: {businessName}
      }
    } = this.props
    const isClosed = this.isSurveyClosed(userSurvey)

    return (
      <Wrapper>
        <Loading id={idNamespace} load={this.state.load} Error={() => (this.state.userSurvey ? <Unknown /> : <Http404 />)}>
          {!isClosed && status === STARTED && (
            <QuestionContainer
              id={idNamespace}
              userSurvey={userSurvey}
              createUserSurveyComment={this.createUserSurveyComment}
              updateUserSurvey={(data, syncLoadToState) => this.updateUserSurvey(this.state.userSurvey, data, syncLoadToState)} />
          )}

          {!isClosed && status === FINISHED && (
            <Finish id={idNamespace} userSurvey={userSurvey} isReturningUser={this.state.initiallyFinished} tenantName={businessName} />
          )}

          {isClosed && <Expired id={idNamespace} userSurvey={userSurvey} />}
        </Loading>
      </Wrapper>
    )
  }
}

export const mapStateToProps = ({survey}) => ({survey})

export const mapDispatchToProps = (dispatch) => ({
  dispatchDriverList: (drivers) => dispatch(updateDriverList(drivers))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withUnauthed(UserSurvey))
